import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, HelpCircle, Loader2, X, RotateCcw } from "lucide-react";
import {
  DIRS,
  occupancy,
  pieceCanExit,
  tap,
  newGame,
  nextLevel,
  restart,
  sanitizeProgress,
} from "@/lib/escapebees";

const CELL = 40; // user units; the whole board scales to the container width
const LINE = "#1f2937"; // arrows are thin near-black lines (see example)
const LINE_W = CELL * 0.11;

/* ── little chevron pointing in a direction ─────────────────────────────────── */
function Chevron({ cx, cy, dir, size = 12, color = "rgba(0,0,0,0.55)" }) {
  const [dr, dc] = DIRS[dir];
  // tip in the facing direction, two tails behind
  const tip = [cx + dc * size, cy + dr * size];
  const perp = [dr === 0 ? 1 : 0, dc === 0 ? 1 : 0]; // perpendicular axis
  const back = [cx - dc * size * 0.4, cy - dr * size * 0.4];
  const a = [back[0] + perp[1] * size, back[1] + perp[0] * size];
  const b = [back[0] - perp[1] * size, back[1] - perp[0] * size];
  return <polygon points={`${tip[0]},${tip[1]} ${a[0]},${a[1]} ${b[0]},${b[1]}`} fill={color} />;
}

/* ── bee drawn inside a cell ─────────────────────────────────────────────────── */
function Bee({ x, y, dir }) {
  const cx = x + CELL / 2;
  const cy = y + CELL / 2;
  const r = CELL * 0.26;
  return (
    <g>
      {/* wings */}
      <ellipse cx={cx - r * 0.5} cy={cy - r * 0.7} rx={r * 0.6} ry={r * 0.42} fill="#E0F2FE" stroke="#BAE6FD" opacity="0.95" />
      <ellipse cx={cx + r * 0.5} cy={cy - r * 0.7} rx={r * 0.6} ry={r * 0.42} fill="#E0F2FE" stroke="#BAE6FD" opacity="0.95" />
      {/* body */}
      <ellipse cx={cx} cy={cy} rx={r * 1.1} ry={r} fill="#F6B93B" stroke="#7C4A03" strokeWidth="1.5" />
      <rect x={cx - r * 0.35} y={cy - r} width={r * 0.5} height={r * 2} rx="2" fill="#5B3A08" />
      <rect x={cx + r * 0.35} y={cy - r * 0.9} width={r * 0.42} height={r * 1.8} rx="2" fill="#5B3A08" />
      {/* head */}
      <circle cx={cx - r * 1.05} cy={cy} r={r * 0.5} fill="#3F2A07" />
      {/* facing indicator */}
      <Chevron cx={cx + DIRS[dir][1] * r * 1.7} cy={cy + DIRS[dir][0] * r * 1.7} dir={dir} size={7} color="#7C4A03" />
    </g>
  );
}

const ctr = ([r, c]) => [c * CELL + CELL / 2, r * CELL + CELL / 2];
const fmtPts = (pts) => "M " + pts.map((p) => p.map((n) => n.toFixed(1)).join(" ")).join(" L ");

// cells the head must travel to clear the board edge in its facing direction
const toEdgeCells = (piece, W, H) => {
  const h = piece.cells[piece.cells.length - 1];
  return piece.dir === "up" ? h[0] : piece.dir === "down" ? H - 1 - h[0] : piece.dir === "left" ? h[1] : W - 1 - h[1];
};
// slither duration — longer snakes / longer runs take a touch more time
const ghostDurMs = (piece, W, H) => (piece.bee ? 450 : Math.min(1150, Math.max(360, (piece.cells.length + toEdgeCells(piece, W, H)) * 46)));

const HEAD_BACK = 0.08; // where the arrowhead base sits, behind the head-cell centre

// The shaft path for an arrow (cells + a small stub for single-cell dots). The
// shaft stops at the arrowhead base so the two meet cleanly.
function shaftPath(piece) {
  const [dr, dc] = DIRS[piece.dir];
  const pts = piece.cells.map(ctr);
  const head = pts[pts.length - 1];
  const shaft = pts.slice();
  shaft[shaft.length - 1] = [head[0] - dc * CELL * HEAD_BACK, head[1] - dr * CELL * HEAD_BACK];
  if (pts.length === 1) shaft.unshift([head[0] - dc * CELL * 0.32, head[1] - dr * CELL * 0.32]);
  return shaft;
}

// A bold triangular arrowhead filling the head cell, pointing in `dir`.
function arrowHead(piece) {
  const [dr, dc] = DIRS[piece.dir];
  const head = ctr(piece.cells[piece.cells.length - 1]);
  const tip = [head[0] + dc * CELL * 0.44, head[1] + dr * CELL * 0.44];
  const back = [head[0] - dc * CELL * HEAD_BACK, head[1] - dr * CELL * HEAD_BACK];
  const w = CELL * 0.3; // half the base width
  const px = -dc;
  const py = dr; // perpendicular
  const a = [back[0] + px * w, back[1] + py * w];
  const b = [back[0] - px * w, back[1] - py * w];
  const pt = (p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`;
  return `${pt(tip)} ${pt(a)} ${pt(b)}`;
}

/* ── one live piece: an arrow drawn as a thin line + head, or a bee ─────────── */
function Piece({ piece, freeBee, onTap, className, style }) {
  const hit = piece.cells.map(([r, c], i) => <rect key={`h${i}`} x={c * CELL} y={r * CELL} width={CELL} height={CELL} fill="transparent" />);

  if (piece.bee) {
    const [r, c] = piece.cells[0];
    const cx = c * CELL + CELL / 2;
    const cy = r * CELL + CELL / 2;
    return (
      <g className={className} style={{ cursor: "pointer", ...style }} onClick={() => onTap(piece.id)} data-testid={`esc-piece-${piece.id}`}>
        {freeBee && <circle cx={cx} cy={cy} r={CELL * 0.42} fill="#FEF3C7" stroke="#F59E0B" strokeWidth="2.5" className="esc-freebee" />}
        <Bee x={c * CELL} y={r * CELL} dir={piece.dir} />
        {hit}
      </g>
    );
  }

  const d = fmtPts(shaftPath(piece));
  return (
    <g className={className} style={{ cursor: "pointer", ...style }} onClick={() => onTap(piece.id)} data-testid={`esc-piece-${piece.id}`}>
      <path d={d} fill="none" stroke={LINE} strokeWidth={LINE_W} strokeLinecap="round" strokeLinejoin="round" />
      <polygon points={arrowHead(piece)} fill={LINE} stroke={LINE} strokeWidth={LINE_W * 0.5} strokeLinejoin="round" strokeLinecap="round" />
      {hit}
    </g>
  );
}

/* ── a piece slithering out head-first, tail following the path like a snake ─── */
function SnakeGhost({ piece, W, H }) {
  const [dr, dc] = DIRS[piece.dir];
  if (piece.bee) {
    // bees just fly out in their facing direction
    const [r, c] = piece.cells[0];
    return (
      <g style={{ animation: `esc-fly-${piece.dir} .45s ease-in forwards` }}>
        <Bee x={c * CELL} y={r * CELL} dir={piece.dir} />
      </g>
    );
  }
  const te = toEdgeCells(piece, W, H);
  const pts = piece.cells.map(ctr);
  const start = ctr(piece.cells[piece.cells.length - 1]);
  const ext = te + piece.cells.length + 2;
  for (let k = 1; k <= ext; k++) pts.push([start[0] + dc * CELL * k, start[1] + dr * CELL * k]);
  const d = fmtPts(pts);
  const bodyLen = Math.max(CELL, (piece.cells.length - 1) * CELL) + LINE_W;
  const total = (pts.length - 1) * CELL;
  const durS = ghostDurMs(piece, W, H) / 1000;
  return (
    <path
      d={d}
      fill="none"
      stroke={LINE}
      strokeWidth={LINE_W}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeDasharray={`${bodyLen.toFixed(1)} ${(total + bodyLen + CELL).toFixed(1)}`}
      style={{ "--slither": `${(-total).toFixed(1)}`, animation: `esc-slither ${durS.toFixed(2)}s linear forwards` }}
    />
  );
}

export default function EscapeBees() {
  const [s, setS] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [won, setWon] = useState(false);
  const [shaking, setShaking] = useState(null);
  const [ghosts, setGhosts] = useState([]); // [{ key, piece }]
  const [homePulse, setHomePulse] = useState(false);
  const stateRef = useRef(null);
  const ghostId = useRef(0);

  useEffect(() => {
    stateRef.current = s;
  }, [s]);

  // load
  useEffect(() => {
    let cancelled = false;
    api
      .get("/games/escapebees")
      .then((r) => {
        if (cancelled) return;
        const p = sanitizeProgress(r.data);
        setS(newGame(p.level, p.beesHome, p.best));
      })
      .catch(() => setS(newGame(1)))
      .finally(() => !cancelled && setLoaded(true));
    return () => {
      cancelled = true;
    };
  }, []);

  // debounced save (level + lifetime stats only; the board regenerates)
  const saveTimer = useRef(null);
  useEffect(() => {
    if (!loaded || !s) return undefined;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.put("/games/escapebees", { level: s.level, beesHome: s.beesHome, best: s.best }).catch(() => {});
    }, 500);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
  }, [loaded, s]);

  const flyGhost = useCallback((piece) => {
    const cur = stateRef.current;
    const W = cur ? cur.W : 16;
    const H = cur ? cur.H : 16;
    const key = ++ghostId.current;
    setGhosts((g) => [...g, { key, piece }]);
    setTimeout(() => setGhosts((g) => g.filter((x) => x.key !== key)), ghostDurMs(piece, W, H) + 120);
  }, []);

  const onTap = useCallback(
    (id) => {
      const cur = stateRef.current;
      if (!cur || won) return;
      const res = tap(cur, id);
      if (!res.ok) {
        setShaking(id);
        setTimeout(() => setShaking((v) => (v === id ? null : v)), 380);
        return;
      }
      flyGhost(res.piece);
      if (res.bee) {
        setHomePulse(true);
        setTimeout(() => setHomePulse(false), 500);
      }
      setS(res.state);
      if (res.won) {
        // send the remaining arrows off too, then celebrate
        res.state.pieces.forEach((p) => flyGhost(p));
        setS({ ...res.state, pieces: [] });
        setTimeout(() => setWon(true), 320);
      }
    },
    [won, flyGhost]
  );

  const goNext = useCallback(() => {
    setWon(false);
    setGhosts([]);
    setS((prev) => nextLevel(prev));
  }, []);

  const resetLevel = useCallback(() => {
    setWon(false);
    setGhosts([]);
    setS((prev) => restart(prev));
  }, []);

  // pieces that can currently leave (used to highlight free bees)
  const freeSet = useMemo(() => {
    if (!s) return new Set();
    const occ = occupancy(s.pieces);
    const set = new Set();
    for (const p of s.pieces) if (pieceCanExit(p, occ, s.W, s.H)) set.add(p.id);
    return set;
  }, [s]);

  if (!loaded || !s) {
    return (
      <div className="grid min-h-[60vh] place-items-center text-stone-400">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const boardW = s.W * CELL;
  const boardH = s.H * CELL;
  const beesLeft = s.pieces.filter((p) => p.bee).length;

  return (
    <div className="mx-auto max-w-4xl space-y-4" data-testid="escapebees-page">
      <style>{`
        @keyframes esc-shake { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-3px) rotate(-2deg)} 40%{transform:translateX(3px) rotate(2deg)} 60%{transform:translateX(-2px)} 80%{transform:translateX(2px)} }
        .esc-shake { animation: esc-shake .38s ease-in-out; }
        @keyframes esc-freebee { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-2px)} }
        .esc-freebee { animation: esc-freebee 1.1s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
        @keyframes esc-fly-up    { to { transform: translateY(-620px); opacity:0 } }
        @keyframes esc-fly-down  { to { transform: translateY(620px);  opacity:0 } }
        @keyframes esc-fly-left  { to { transform: translateX(-620px); opacity:0 } }
        @keyframes esc-fly-right { to { transform: translateX(620px);  opacity:0 } }
        @keyframes esc-slither { to { stroke-dashoffset: var(--slither); } }
        @keyframes esc-pop { 0%{transform:scale(1)} 40%{transform:scale(1.35)} 100%{transform:scale(1)} }
        .esc-pop { animation: esc-pop .5s ease-out; transform-origin:center; display:inline-block; }
      `}</style>

      {/* header */}
      <div className="flex items-center justify-between">
        <Link to="/app/games" className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800">
          <ArrowLeft className="h-4 w-4" /> Games
        </Link>
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-amber-100 px-3 py-1 text-sm font-semibold text-amber-700" data-testid="esc-level">Level {s.level}</span>
          <Button variant="outline" size="sm" onClick={resetLevel} data-testid="escapebees-reset"><RotateCcw className="mr-1 h-3.5 w-3.5" /> Restart</Button>
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setShowHelp(true)}><HelpCircle className="h-4 w-4" /></Button>
        </div>
      </div>

      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-stone-800">Escape Bees 🐝</h1>
        <p className="mt-1 text-stone-500">Tap an arrow and it slithers out of the maze head-first — but only if the way ahead of its head is clear. Free every trapped bee.</p>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_220px]">
        {/* board */}
        <div className="rounded-2xl border border-stone-200 bg-white p-3 sm:p-4">
          <div className="mx-auto" style={{ maxWidth: boardW }}>
            <svg viewBox={`0 0 ${boardW} ${boardH}`} width="100%" style={{ display: "block", overflow: "hidden" }} data-testid="esc-board">
              <defs>
                <clipPath id="esc-board-clip">
                  <rect x="0" y="0" width={boardW} height={boardH} rx="8" />
                </clipPath>
              </defs>
              <rect x="0" y="0" width={boardW} height={boardH} rx="8" fill="#fff" />
              {/* arrows are clipped to the board, so they visibly slither out at the edge */}
              <g clipPath="url(#esc-board-clip)">
                {s.pieces.map((p) => (
                  <Piece key={p.id} piece={p} freeBee={p.bee && freeSet.has(p.id)} onTap={onTap} className={shaking === p.id ? "esc-shake" : ""} />
                ))}
                {ghosts.map((g) => (
                  <SnakeGhost key={g.key} piece={g.piece} W={s.W} H={s.H} />
                ))}
              </g>
            </svg>
          </div>
        </div>

        {/* hive / home counter */}
        <div className="space-y-3">
          <div className="rounded-2xl border border-stone-200 bg-white p-4 text-center">
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-stone-400">Hive</div>
            {/* skep */}
            <svg viewBox="0 0 120 110" width="100%" style={{ maxWidth: 130, margin: "0 auto", display: "block" }}>
              <ellipse cx="60" cy="104" rx="46" ry="6" fill="rgba(0,0,0,0.06)" />
              {[{ y: 78, w: 84 }, { y: 62, w: 76 }, { y: 47, w: 64 }, { y: 33, w: 48 }, { y: 21, w: 30 }].map((b, i) => (
                <rect key={i} x={60 - b.w / 2} y={b.y} width={b.w} height={i === 0 ? 20 : 16} rx="9" fill={i % 2 ? "#E8A93A" : "#F2BC53"} stroke="#C6871F" strokeWidth="1.5" />
              ))}
              <ellipse cx="60" cy="80" rx="9" ry="12" fill="#5B3A08" />
              <text x="60" y="14" textAnchor="middle" fontSize="14">🐝</text>
            </svg>
            <div className={`mt-2 text-4xl font-bold text-amber-600 ${homePulse ? "esc-pop" : ""}`} data-testid="esc-home">{s.beesHome}</div>
            <div className="text-xs text-stone-500">bees safely home</div>
          </div>

          <div className="rounded-2xl border border-stone-200 bg-white p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-stone-500">This level</span>
              <span className="font-semibold text-stone-800" data-testid="esc-progress">{s.beesThisLevel} / {s.beesTotal}</span>
            </div>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-stone-100">
              <div className="h-full bg-amber-400 transition-all" style={{ width: `${s.beesTotal ? (s.beesThisLevel / s.beesTotal) * 100 : 0}%` }} />
            </div>
            <div className="mt-2 text-xs text-stone-400">{beesLeft} bee{beesLeft === 1 ? "" : "s"} still trapped · best level {s.best}</div>
          </div>
        </div>
      </div>

      {/* win overlay */}
      {won && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={goNext}>
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="text-5xl">🐝🎉</div>
            <h2 className="mt-3 font-display text-2xl font-semibold text-stone-800">All bees free!</h2>
            <p className="mt-1 text-stone-500">You freed {s.beesTotal} bee{s.beesTotal === 1 ? "" : "s"} on Level {s.level}.</p>
            <div className="mt-5 flex gap-2">
              <Button variant="outline" className="flex-1" onClick={resetLevel}>Replay</Button>
              <Button className="flex-1" onClick={goNext} data-testid="esc-next">Next level →</Button>
            </div>
          </div>
        </div>
      )}

      {/* help */}
      {showHelp && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={() => setShowHelp(false)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-display text-xl font-semibold">How to play</h2>
              <button onClick={() => setShowHelp(false)} className="text-stone-400 hover:text-stone-600"><X className="h-5 w-5" /></button>
            </div>
            <ul className="list-disc space-y-2 pl-5 text-sm text-stone-600">
              <li>Each <b>arrow</b> is a line — short or long, straight or twisting — that <b>slithers out head-first</b> in the direction its head points, its tail following its own trail.</li>
              <li>Tap an arrow to send it out — but it only moves if <b>the cells ahead of its head are clear to the edge</b>. Blocked arrows just wiggle.</li>
              <li><b>Bees</b> (the glowing ones) are fenced in. Clear the arrows around a bee, then tap it to fly it out the way it faces.</li>
              <li>The <b>hive</b> counts every bee that makes it home. Free every bee to clear the level.</li>
              <li>You can never get stuck — there's always a next move. Later levels are bigger, with more bees and longer, twistier arrows.</li>
            </ul>
            <Button className="mt-5 w-full" onClick={() => setShowHelp(false)}>Got it</Button>
          </div>
        </div>
      )}
    </div>
  );
}
